import { useState, useMemo } from 'react'
import { useVendas } from '../hooks/useVendas'

const METAS_MANUAL = {
  'Pedro': 175000,
  'Gabriel': 175000,
  'Targino': 80000,
  'Arthur': 80000,
  'Caua': 50000,
}

const DIAS_TRABALHO = 27

export default function Pace() {
  const { vendas, loading } = useVendas(7, 2026) // Julho 2026
  const [dataInicio, setDataInicio] = useState('2026-07-01')
  const [dataFim, setDataFim] = useState(new Date().toISOString().split('T')[0])

  const hoje = new Date()
  const diaAtual = hoje.getDate()

  // Filtra vendas do período
  const vendasPeriodo = useMemo(() => {
    if (!vendas || vendas.length === 0) return []

    return vendas.filter(venda => {
      const dataVenda = new Date(venda.data)
      const dataInicioObj = new Date(dataInicio)
      const dataFimObj = new Date(dataFim)

      return dataVenda >= dataInicioObj && dataVenda <= dataFimObj
    })
  }, [vendas, dataInicio, dataFim])

  // Calcula dados por vendedor
  const dadosPorVendedor = useMemo(() => {
    const grupos: {
      [key: string]: {
        vendas: number
        faturamento: number
      }
    } = {}

    vendasPeriodo.forEach(venda => {
      const vendedor = venda.vendedor || 'Desconhecido'
      if (!grupos[vendedor]) {
        grupos[vendedor] = { vendas: 0, faturamento: 0 }
      }
      grupos[vendedor].vendas += 1
      grupos[vendedor].faturamento += venda.valor
    })

    // Calcula métricas
    const resultado = Object.entries(grupos).map(([vendedor, dados]) => {
      const meta = METAS_MANUAL[vendedor as keyof typeof METAS_MANUAL] || 0
      const ticketMedio = dados.vendas > 0 ? dados.faturamento / dados.vendas : 0
      const projecao = (dados.faturamento / diaAtual) * DIAS_TRABALHO
      const percentualAtingimento = meta > 0 ? (dados.faturamento / meta) * 100 : 0
      const metaDiaria = meta / DIAS_TRABALHO
      const metaAcumuladaAteHoje = metaDiaria * diaAtual
      const falta = metaAcumuladaAteHoje - dados.faturamento

      return {
        vendedor,
        realizado: dados.faturamento,
        meta,
        projecao,
        percentualAtingimento,
        vendas: dados.vendas,
        ticketMedio,
        metaDiaria,
        metaAcumuladaAteHoje,
        falta,
      }
    })

    return resultado.sort((a, b) => b.realizado - a.realizado)
  }, [vendasPeriodo, diaAtual])

  // Calcula totais
  const totais = useMemo(() => {
    const realizado = dadosPorVendedor.reduce((sum, item) => sum + item.realizado, 0)
    const metaTotal = Object.values(METAS_MANUAL).reduce((sum, val) => sum + val, 0)
    const vendas = dadosPorVendedor.reduce((sum, item) => sum + item.vendas, 0)
    const ticketMedio = vendas > 0 ? realizado / vendas : 0
    const projecao = (realizado / diaAtual) * DIAS_TRABALHO
    const percentualAtingimento = metaTotal > 0 ? (realizado / metaTotal) * 100 : 0

    return {
      realizado,
      metaTotal,
      projecao,
      percentualAtingimento,
      vendas,
      ticketMedio,
    }
  }, [dadosPorVendedor, diaAtual])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-gray-600 dark:text-gray-400">Carregando dados...</div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-auto p-6 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Pace - Realizado x Projetado</h1>

          {/* Filtros */}
          <div className="flex gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data Início</label>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data Fim</label>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <div className="flex items-end">
              <span className="text-sm text-gray-600 dark:text-gray-400">Dia atual: {diaAtual}/{DIAS_TRABALHO}</span>
            </div>
          </div>
        </div>

        {/* Seção Geral */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">📊 Visão Geral</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Card Realizado */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">Realizado</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                R$ {(totais.realizado / 1000).toFixed(0)}k
              </p>
              <p className="text-xs text-gray-500 mt-2">Total vendido até agora</p>
            </div>

            {/* Card Meta */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">Meta</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                R$ {(totais.metaTotal / 1000).toFixed(0)}k
              </p>
              <p className="text-xs text-gray-500 mt-2">Meta do período</p>
            </div>

            {/* Card Projeção */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">Projeção</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                R$ {(totais.projecao / 1000).toFixed(0)}k
              </p>
              <p className="text-xs text-gray-500 mt-2">(ritmo atual × 27 dias)</p>
            </div>

            {/* Card % Atingimento */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">Atingimento</p>
              <p className={`text-3xl font-bold ${
                totais.percentualAtingimento >= 100 ? 'text-green-600' :
                totais.percentualAtingimento >= 75 ? 'text-blue-600' :
                totais.percentualAtingimento >= 50 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {totais.percentualAtingimento.toFixed(1)}%
              </p>
              <p className="text-xs text-gray-500 mt-2">Da meta total</p>
            </div>
          </div>

          {/* Barra de Progresso Geral */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6">
            <div className="mb-2 flex justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Progresso Geral</span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                R$ {(totais.realizado / 1000).toFixed(1)}k de R$ {(totais.metaTotal / 1000).toFixed(0)}k
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-8 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all flex items-center justify-center text-white text-xs font-bold ${
                  totais.percentualAtingimento >= 100 ? 'bg-green-600' :
                  totais.percentualAtingimento >= 75 ? 'bg-blue-600' :
                  totais.percentualAtingimento >= 50 ? 'bg-yellow-600' : 'bg-red-600'
                }`}
                style={{ width: `${Math.min(totais.percentualAtingimento, 100)}%` }}
              >
                {totais.percentualAtingimento > 10 && `${totais.percentualAtingimento.toFixed(0)}%`}
              </div>
            </div>
          </div>

          {/* Métricas Gerais */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
              <p className="text-gray-600 dark:text-gray-400 text-xs font-medium mb-1">Total de Vendas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totais.vendas}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
              <p className="text-gray-600 dark:text-gray-400 text-xs font-medium mb-1">Ticket Médio</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                R$ {(totais.ticketMedio).toFixed(0)}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
              <p className="text-gray-600 dark:text-gray-400 text-xs font-medium mb-1">Valor Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                R$ {(totais.realizado / 1000).toFixed(1)}k
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
              <p className="text-gray-600 dark:text-gray-400 text-xs font-medium mb-1">Média/Dia</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                R$ {(totais.realizado / diaAtual / 1000).toFixed(1)}k
              </p>
            </div>
          </div>
        </div>

        {/* Seção Individual */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">👤 Por Vendedor</h2>

          <div className="overflow-x-auto">
            <table className="w-full bg-white dark:bg-gray-800 rounded-lg shadow">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">Vendedor</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-white">Realizado</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-white">Meta</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-white">Projeção</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 dark:text-white">% Atingimento</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-white">Vendas</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-white">Ticket Médio</th>
                </tr>
              </thead>
              <tbody>
                {dadosPorVendedor.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 text-gray-900 dark:text-white font-medium">{item.vendedor}</td>
                    <td className="px-6 py-4 text-right text-gray-900 dark:text-white">
                      R$ {(item.realizado / 1000).toFixed(1)}k
                    </td>
                    <td className="px-6 py-4 text-right text-gray-900 dark:text-white">
                      R$ {(item.meta / 1000).toFixed(0)}k
                    </td>
                    <td className="px-6 py-4 text-right text-gray-900 dark:text-white">
                      R$ {(item.projecao / 1000).toFixed(1)}k
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                        item.percentualAtingimento >= 100 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        item.percentualAtingimento >= 75 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        item.percentualAtingimento >= 50 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {item.percentualAtingimento.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-gray-900 dark:text-white">{item.vendas}</td>
                    <td className="px-6 py-4 text-right text-gray-900 dark:text-white">
                      R$ {item.ticketMedio.toFixed(0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Progresso por Vendedor */}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {dadosPorVendedor.map((item, idx) => (
              <div key={idx} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{item.vendedor}</h3>
                  <span className={`text-sm font-bold px-2 py-1 rounded ${
                    item.percentualAtingimento >= 100 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    item.percentualAtingimento >= 75 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    item.percentualAtingimento >= 50 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {item.percentualAtingimento.toFixed(0)}%
                  </span>
                </div>

                <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex justify-between mb-1">
                    <span>Realizado:</span>
                    <span className="font-semibold">R$ {(item.realizado / 1000).toFixed(1)}k</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Meta Acumulada até hoje:</span>
                    <span className="font-semibold">R$ {(item.metaAcumuladaAteHoje / 1000).toFixed(1)}k</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Meta Diária:</span>
                    <span className="font-semibold">R$ {(item.metaDiaria / 1000).toFixed(1)}k/dia</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Meta Total:</span>
                    <span className="font-semibold">R$ {(item.meta / 1000).toFixed(0)}k</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Projeção:</span>
                    <span className="font-semibold">R$ {(item.projecao / 1000).toFixed(1)}k</span>
                  </div>
                  <div className={`flex justify-between pt-2 border-t border-gray-300 dark:border-gray-600 ${
                    item.falta > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                  }`}>
                    <span className="font-bold">{item.falta > 0 ? 'Falta:' : 'Adiantado:'}</span>
                    <span className="font-bold">R$ {Math.abs(item.falta / 1000).toFixed(1)}k</span>
                  </div>
                </div>

                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-6 overflow-hidden mb-2">
                  <div
                    className={`h-full rounded-full transition-all flex items-center justify-center text-white text-xs font-bold ${
                      item.percentualAtingimento >= 100 ? 'bg-green-600' :
                      item.percentualAtingimento >= 75 ? 'bg-blue-600' :
                      item.percentualAtingimento >= 50 ? 'bg-yellow-600' : 'bg-red-600'
                    }`}
                    style={{ width: `${Math.min(item.percentualAtingimento, 100)}%` }}
                  >
                    {item.percentualAtingimento > 15 && `${item.percentualAtingimento.toFixed(0)}%`}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    <p className="text-gray-600 dark:text-gray-400">Vendas</p>
                    <p className="font-bold text-gray-900 dark:text-white">{item.vendas}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    <p className="text-gray-600 dark:text-gray-400">Ticket</p>
                    <p className="font-bold text-gray-900 dark:text-white">R$ {item.ticketMedio.toFixed(0)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
